import { renderPrescriptionPdf, downloadPrescriptionPdf, printPrescriptionPdf } from "@/utils/prescriptionPdf.js";
import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
// src/pages/prescriptions/PatientPrescriptionDetails.jsx
import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import TopBarRx from "@/sot/primitives/TopBarRx.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Divider from "@/components/ui/Divider.jsx";
import {
  buildPrescriptionDosageLabel,
  buildPrescriptionScheduleRows,
} from "@/utils/prescriptionSchedule.js";
import { selectRxFrequencyLabel } from "@/utils/rxLabelUtils";
import {
  selectDoctorDisplayName,
  selectDoctorLicenseNumber,
  selectDoctorPrescriptionName,
} from "@/utils/prescriptionDoctorIdentity.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import {
  buildPrescriptionVerifySnapshot,
  buildPrescriptionVerifyPath,
  buildPrescriptionVerifyState,
  buildPrescriptionVerifyUrl,
} from "@/utils/prescriptionVerification.js";
import { resolveFacilityAddress } from "@/utils/prescriptionFacility.js";
import { makeBrandedQrDataUrl } from "@/utils/brandedQr.js";
import {
  listPharmacyBranches,
  requestPharmacyFulfillment,
  reversePharmacyPayment,
} from "@/lib/payments/api.ts";
import { resolvePaymentCurrency } from "@/services/marketPaymentService.js";
import { formatCurrency, formatCurrencyFromCents } from "@/utils/formatCurrency.js";
import nearbyHealthcareService from "@/services/nearbyHealthcareService.js";
import supabase from "@/services/supabaseClient"; 
import {
  hasCanonicalPharmacyAssignment,
  resolveCanonicalFulfillmentState,
} from "@/lib/pharmacyFulfillmentTruth.mjs";
import { parseISO } from "date-fns";
import QrOverlay from "@/components/prescriptions/QrOverlay.jsx";
import SignatureStampLine from "@/components/prescriptions/SignatureStampLine.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

// assets
const ICON_PRINT = "/icons/appointments/e-prescription/print-filled.svg";
const ICON_DOWNLOAD = "/icons/appointments/e-prescription/download.svg";
const ICON_QR = "/icons/appointments/e-prescription/qr-code-filled.svg";
const SIG_PLACEHOLDER = "/icons/appointments/e-prescription/Signature.svg";
const QR_ELEPHANT = "/afrimed_elephant_icon.svg";

// ✅ We only use the BASE elephant PNG + vector rings/text overlay (DoctorStamp)
// (The fully baked SVG/PNG stamp assets are intentionally not used to avoid double layers)
const DR_STAMP_BASE = "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png";

// QR overlay mock (later replaced by real generated QR)
const QR_OVERLAY = "/icons/appointments/e-prescription/qr-code-overlay.svg";

const abs = (p) =>
  /^(?:https?:|data:|blob:)/i.test(String(p || "").trim())
    ? String(p || "").trim()
    : `${window.location.origin}${p}`;

function safeDate(d) {
  if (d instanceof Date) {
    return !Number.isNaN(d.getTime()) ? d : null;
  }
  if (d === null || d === undefined || d === "") return null;
  const parsed = new Date(d);
  return !Number.isNaN(parsed.getTime()) ? parsed : null;
}

function fmtDate(d) {
  const dd = safeDate(d);
  if (!dd) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(dd);
  } catch {
    return "—";
  }
}

function fmtShortDate(d) {
  const dd = safeDate(d);
  if (!dd) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(dd);
  } catch {
    return "";
  }
}

function formatFulfillmentModeLabel(value = "", t) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return t("prescriptions.fulfillment.awaitingSelection", "Awaiting selection");
  return normalized === "delivery"
    ? t("prescriptions.fulfillment.modeDelivery", "Delivery")
    : t("prescriptions.fulfillment.modePickup", "Pickup");
}

function localizeMedicationFormLabel(value = "", t) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const replacements = [
    ["tablet", t("prescriptions.wizard.medType.tablet", "Tablet")],
    ["capsule", t("prescriptions.wizard.medType.capsule", "Capsule")],
    ["injection solution", t("prescriptions.wizard.medType.injectionSolution", "Injection solution")],
    ["oral solution", t("prescriptions.wizard.medType.oralSolution", "Oral solution")],
    ["topical", t("prescriptions.wizard.medType.topical", "Topical")],
    ["device", t("prescriptions.wizard.medType.device", "Device")],
    ["drops", t("prescriptions.wizard.medType.drops", "Drops")],
    ["inhaler", t("prescriptions.wizard.medType.inhaler", "Inhaler")],
  ];

  return replacements.reduce((output, [english, localized]) => {
    const pattern = new RegExp(`\\b${english}s?\\b`, "ig");
    return output.replace(pattern, localized);
  }, raw);
}

function buildLocalizedMedicationItems(items = [], t, languageCode = "") {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const schedule = {
        frequencyType: String(item.schedule?.frequencyType || item.frequencyType || "").trim().toLowerCase(),
        everyHours: item.schedule?.everyHours ?? item.everyHours ?? null,
        specificDays: Array.isArray(item.schedule?.specificDays)
          ? item.schedule.specificDays
          : Array.isArray(item.specificDays)
            ? item.specificDays
            : [],
        cycleUseDays: item.schedule?.cycleUseDays ?? item.cycleUseDays ?? null,
        cyclePauseDays: item.schedule?.cyclePauseDays ?? item.cyclePauseDays ?? null,
        startDateIso: item.schedule?.startDateIso || item.startsOn || "",
        endDateIso: item.schedule?.endDateIso || item.endsOn || "",
        dosesPerTime: item.schedule?.dosesPerTime || item.dosesPerTime || "",
        doseUnitLabel: item.schedule?.doseUnitLabel || item.doseUnitLabel || item.form || "",
      };

      return {
        ...item,
        form: localizeMedicationFormLabel(item.form || item.typeLabel || "", t),
        schedule,
        frequencyLabel:
          item.frequencyLabel || selectRxFrequencyLabel({ schedule, t }) || "—",
        dosageLabel:
          buildPrescriptionDosageLabel(
            {
              dosageLabel: item.dosageLabel || "",
              strengthValue: item.strengthValue || "",
              strengthUnit: item.strengthUnit || "",
              strengthLabel: item.strengthLabel || item.form || "",
              dosesPerTime: schedule.dosesPerTime,
              doseUnitLabel: schedule.doseUnitLabel,
              schedule,
            },
            { t, languageCode },
          ) || "—",
      };
    });
}

function buildLocalizedPrescriptionView(value, t, languageCode = "") {
  if (!value || typeof value !== "object") return value;

  const meta = value?.meta && typeof value.meta === "object" ? value.meta : {};
  const inputSchedule =
    value?.schedule && typeof value.schedule === "object" ? value.schedule : {};
  const schedule = {
    frequencyType: String(
      inputSchedule.frequencyType || meta.frequencyType || value.frequencyType || "",
    ).trim().toLowerCase(),
    everyHours: inputSchedule.everyHours ?? meta.everyHours ?? value.everyHours ?? null,
    specificDays: Array.isArray(inputSchedule.specificDays)
      ? inputSchedule.specificDays
      : Array.isArray(meta.specificDays)
        ? meta.specificDays
        : [],
    cycleUseDays: inputSchedule.cycleUseDays ?? meta.cycleUseDays ?? value.cycleUseDays ?? null,
    cyclePauseDays:
      inputSchedule.cyclePauseDays ?? meta.cyclePauseDays ?? value.cyclePauseDays ?? null,
    startDateIso:
      inputSchedule.startDateIso ||
      meta.startsOn ||
      value.startDateIso ||
      value.startsOn ||
      "",
    endDateIso:
      inputSchedule.endDateIso ||
      meta.endsOn ||
      value.endDateIso ||
      value.endsOn ||
      "",
    dosesPerTime: inputSchedule.dosesPerTime || meta.dosesPerTime || value.dosesPerTime || "",
    doseUnitLabel:
      inputSchedule.doseUnitLabel || meta.doseUnitLabel || value.doseUnitLabel || "",
  };

  const localizedFrequencyLabel =
    selectRxFrequencyLabel({ schedule, t }) ||
    String(value.frequencyLabel || meta.frequencyLabel || meta.frequency || "").trim() ||
    "—";
  const localizedDosageLabel =
    buildPrescriptionDosageLabel(
      {
        dosageLabel: value.dosageLabel || meta.dosageLabel || meta.dosage || "",
        strengthValue: meta.strengthValue || value.strengthValue || "",
        strengthUnit: meta.strengthUnit || value.strengthUnit || "",
        strengthLabel:
          meta.strengthLabel || value.strengthLabel || value.form || meta.form || "",
        dosesPerTime: schedule.dosesPerTime,
        doseUnitLabel: schedule.doseUnitLabel,
        schedule,
      },
      { t, languageCode },
    ) ||
    "—";

  const localizedMedications = buildLocalizedMedicationItems(
    Array.isArray(value.medications)
      ? value.medications
      : Array.isArray(meta.medications)
        ? meta.medications
        : [],
    t,
    languageCode,
  );
  const primaryMedication = localizedMedications[0] || null;

  return {
    ...value,
    form: localizeMedicationFormLabel(
      value.form || primaryMedication?.form || meta.form || meta.dose_form || meta.strength || "",
      t,
    ),
    medicineName: value.medicineName || primaryMedication?.name || meta.medicineName || meta.medicine || meta.name || "",
    ingredients: value.ingredients || primaryMedication?.composition || meta.ingredients || meta.activeIngredients || meta.composition || "",
    frequencyLabel: primaryMedication?.frequencyLabel || localizedFrequencyLabel,
    dosageLabel: primaryMedication?.dosageLabel || localizedDosageLabel,
    schedule,
    medications: localizedMedications,
  };
}

function buildPatientFulfillmentSummary({
  t,
  fulfillmentStatus = "",
  pharmacyQuoteReady = false,
  pharmacyPaid = false,
  pharmacyRefundPending = false,
  pharmacyRefunded = false,
  pharmacyRequestPending = false,
  pharmacyName = "",
  fulfillmentMode = "pickup",
  pharmacySelectionProven = false,
}) {
  const branchLabel = pharmacyName
    ? displayPharmacyBranchName(pharmacyName)
    : t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy");
  const modeLabel = formatFulfillmentModeLabel(fulfillmentMode, t);
  const isDelivery = String(fulfillmentMode || "").trim().toLowerCase() === "delivery";

  if (!pharmacySelectionProven) {
    return {
      label: t("prescriptions.fulfillment.state.awaitingSelection", "Awaiting pharmacy selection"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.selectPharmacy",
        "A pharmacy still needs to be selected for this prescription.",
      ),
      description: t(
        "prescriptions.fulfillment.description.selectionUnavailable",
        "Pharmacy selection is unavailable for this prescription.",
      ),
      modeLabel: t("prescriptions.fulfillment.awaitingSelection", "Awaiting selection"),
      tone: "info",
    };
  }

  if (fulfillmentStatus === "ready_for_pickup") {
    return {
      label: isDelivery
        ? t("prescriptions.fulfillment.state.readyForDelivery", "Ready for delivery")
        : t("prescriptions.fulfillment.state.ready", "Ready for pickup"),
      nextStep: isDelivery
        ? t(
            "prescriptions.fulfillment.nextStep.delivery",
            "The pharmacy is arranging delivery of your prescription.",
          )
        : t(
            "prescriptions.fulfillment.nextStep.collect",
            "Collect the prescription from the selected pharmacy.",
          ),
      description: isDelivery
        ? t(
            "prescriptions.fulfillment.description.readyForDelivery",
            "{pharmacy} marked this prescription as ready for delivery.",
            { pharmacy: branchLabel },
          )
        : t(
            "prescriptions.fulfillment.description.ready",
            "{pharmacy} marked this prescription as ready for pickup.",
            { pharmacy: branchLabel },
          ),
      modeLabel,
      tone: "success",
    };
  }

  if (fulfillmentStatus === "paid" || pharmacyPaid) {
    return {
      label: t("prescriptions.fulfillment.state.paid", "Paid"),
      nextStep: isDelivery
        ? t(
            "prescriptions.fulfillment.nextStep.waitDeliveryRelease",
            "Wait for the pharmacy to arrange delivery of the order.",
          )
        : t(
            "prescriptions.fulfillment.nextStep.waitRelease",
            "Wait for the pharmacy to mark the order ready for pickup.",
          ),
      description: t(
        "prescriptions.fulfillment.description.paid",
        "Payment is confirmed with {pharmacy}.",
        { pharmacy: branchLabel },
      ),
      modeLabel,
      tone: "success",
    };
  }

  if (fulfillmentStatus === "awaiting_payment" || pharmacyQuoteReady) {
    return {
      label: t("prescriptions.fulfillment.state.paymentReady", "Payment ready"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.pay",
        "Complete payment so the pharmacy can release the order.",
      ),
      description: t(
        "prescriptions.fulfillment.description.paymentReady",
        "{pharmacy} has prepared a payable quote for this prescription.",
        { pharmacy: branchLabel },
      ),
      modeLabel,
      tone: "info",
    };
  }

  if (fulfillmentStatus === "preparing") {
    return {
      label: t("prescriptions.fulfillment.state.preparingAccepted", "Accepted by pharmacy"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.preparing",
        "The pharmacy is preparing the order and will request payment or mark it ready next.",
      ),
      description: t(
        "prescriptions.fulfillment.description.preparing",
        "{pharmacy} accepted this prescription and is preparing it.",
        { pharmacy: branchLabel },
      ),
      modeLabel,
      tone: "info",
    };
  }

  if (fulfillmentStatus === "pharmacy_selected") {
    return {
      label: t("prescriptions.fulfillment.state.reviewing", "Pharmacy reviewing"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.reviewing",
        "Wait for the pharmacy to accept the prescription or set a price.",
      ),
      description: t(
        "prescriptions.fulfillment.description.reviewing",
        "{pharmacy} is reviewing this prescription.",
        { pharmacy: branchLabel },
      ),
      modeLabel,
      tone: "info",
    };
  }

  if (fulfillmentStatus === "rejected") {
    return {
      label: t("prescriptions.fulfillment.state.needsNewPharmacy", "Needs new pharmacy"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.chooseAnother",
        "Choose another signed-up pharmacy to continue.",
      ),
      description: t(
        "prescriptions.fulfillment.description.rejected",
        "{pharmacy} could not fulfil this prescription.",
        { pharmacy: branchLabel },
      ),
      modeLabel,
      tone: "critical",
    };
  }

  if (pharmacyRefundPending) {
    return {
      label: t("prescriptions.fulfillment.state.refundPending", "Refund pending"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.refundPending",
        "Return to this prescription to confirm when the reversal finishes.",
      ),
      description: t(
        "prescriptions.fulfillment.description.refundPending",
        "This pharmacy order is being reversed.",
      ),
      modeLabel,
      tone: "critical",
    };
  }

  if (pharmacyRefunded) {
    return {
      label: t("prescriptions.fulfillment.state.refunded", "Refunded"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.refunded",
        "Choose another pharmacy if you still need this prescription fulfilled.",
      ),
      description: t(
        "prescriptions.fulfillment.description.refunded",
        "This pharmacy order has been refunded.",
      ),
      modeLabel,
      tone: "critical",
    };
  }

  if (pharmacyRequestPending) {
    return {
      label: t("prescriptions.fulfillment.state.requestSent", "Request sent"),
      nextStep: t(
        "prescriptions.fulfillment.nextStep.requestSent",
        "Wait for the selected pharmacy to prepare a quote.",
      ),
      description: t(
        "prescriptions.fulfillment.description.requestSent",
        "{pharmacy} has been asked to prepare a {mode} quote.",
        { pharmacy: branchLabel, mode: modeLabel.toLowerCase() },
      ),
      modeLabel,
      tone: "info",
    };
  }

  return {
    label: t("prescriptions.fulfillment.state.notStarted", "Not started"),
    nextStep: t(
      "prescriptions.fulfillment.nextStep.notStarted",
      "Choose a signed-up pharmacy to start fulfilment.",
    ),
    description: t(
      "prescriptions.fulfillment.description.notStarted",
      "This prescription has not been sent to a pharmacy yet.",
    ),
    modeLabel,
    tone: "default",
  };
}

function buildAddressLines(...values) {
  const lines = [];
  const push = (value) => {
    const text = String(value || "").trim();
    if (!text || text === "—") return;
    const parts = text
      .split(/\r?\n/)
      .map((part) => String(part || "").trim())
      .filter(Boolean);
    parts.forEach((part) => {
      if (!lines.includes(part)) lines.push(part);
    });
  };

  values.forEach(push);
  return lines;
}

function ActionIconButton({ icon, label, onClick, className = "" }) {
  const { isDark } = useTheme();
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={[
        "inline-flex items-center justify-center",
        "h-10 w-10 rounded-[12px]",
        // ✅ no background / no border (AfriMed blueprint)
        "hover:opacity-100 opacity-90",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(2,6,23,0.18)]",
        "transition-opacity",
        className,
      ].join(" ")}
    >
      <img
        src={icon}
        alt=""
        className="h-5 w-5"
        draggable="false"
        style={
          isDark
            ? { filter: "brightness(0) saturate(100%) invert(80%) sepia(8%) saturate(348%) hue-rotate(182deg) brightness(89%) contrast(87%)" }
            : undefined
        }
      />
    </button>
  );
}

function Row({ label, value, withDivider = true }) {
  return (
    <div className="py-3">
      <div className="text-label-medium text-on-surface-var">{label}</div>
      <div className="mt-1 text-body-medium text-on-surface">{value}</div>
      {withDivider ? <div className="mt-3 -mr-4 h-px bg-[rgba(var(--clr-outline-variant),0.55)]" /> : null}
    </div>
  );
}

function formatIssuedScheduleDate(value, languageCode) {
  const date = safeDate(value);
  if (!date) return "—";

  try {
    return new Intl.DateTimeFormat(languageCode || "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return "—";
  }
}

function buildScheduleSummaryItems(source = {}, t = (key, fallback) => fallback, languageCode = "") {
  const schedule = source?.schedule || {};
  const frequencyType = String(schedule.frequencyType || source.frequencyType || "").trim().toLowerCase();
  const everyHours = Number(schedule.everyHours || source.everyHours || 0);
  const frequency =
    frequencyType === "daily"
      ? t("prescriptions.wizard.frequency.daily", "Daily")
      : String(source.frequencyLabel || selectRxFrequencyLabel({ schedule, t }) || "—").trim() || "—";
  const interval = everyHours > 0
    ? `${t("prescriptions.wizard.every", "Every")} ${t("prescriptions.wizard.hoursValue", "{count} hours", { count: everyHours })}`
    : frequencyType === "cyclical"
      ? [
          Number(schedule.cycleUseDays || source.cycleUseDays || 0) > 0
            ? `${t("prescriptions.wizard.useFor", "Use for")} ${Number(schedule.cycleUseDays || source.cycleUseDays)} ${t("prescriptions.wizard.day", "day")}`
            : "",
          Number(schedule.cyclePauseDays || source.cyclePauseDays || 0) > 0
            ? `${t("prescriptions.wizard.pauseFor", "Pause for")} ${Number(schedule.cyclePauseDays || source.cyclePauseDays)} ${t("prescriptions.wizard.day", "day")}`
            : "",
        ].filter(Boolean).join(", ") || "—"
      : "—";

  return [
    { label: t("prescriptions.wizard.frequencyLabel", "Frequency"), value: frequency },
    { label: t("prescriptions.wizard.interval", "Interval"), value: interval },
    {
      label: t("prescriptions.wizard.dosage", "Dosage"),
      value: buildPrescriptionDosageLabel(source, { t, languageCode }) || "—",
    },
    {
      label: t("prescriptions.wizard.starts", "Starts"),
      value: formatIssuedScheduleDate(
        source.startsOn || source.startDateIso || source.meta?.startsOn || schedule.startDateIso,
        languageCode,
      ),
    },
    {
      label: t("prescriptions.wizard.ends", "Ends"),
      value: formatIssuedScheduleDate(
        source.endsOn || source.endDateIso || source.meta?.endsOn || schedule.endDateIso,
        languageCode,
      ),
    },
  ];
}

function ScheduleSummaryGrid({ source }) {
  const { t, languageCode } = useLanguage();
  const items = React.useMemo(
    () => buildScheduleSummaryItems(source, t, languageCode),
    [languageCode, source, t],
  );
  return (
    <div className="px-4">
      {items.map((item, index) => (
        <div key={`${item.label}-${item.value}-${index}`}>
          <div className="flex items-start justify-between gap-6 py-3">
            <div className="text-label-large text-on-surface-var">{item.label}</div>
            <div className="text-body-large text-on-surface text-right">{item.value}</div>
          </div>
          {index < items.length - 1 ? <Divider insetStart={0} insetEnd={0} variant="outlineVariant" /> : null}
        </div>
      ))}
    </div>
  );
}

const DAY_SHORT = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function formatSpecificDays(days) {
  const list = (days || [])
    .map((d) => String(d || "").toLowerCase().slice(0, 3))
    .map((k) => DAY_SHORT[k])
    .filter(Boolean);

  // de-dupe but preserve order
  return [...new Set(list)].join(", ");
}

function buildIdCandidates(...values) {
  return Array.from(
    new Set(
      values
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function formatPharmacyBranchLocation(branch = {}) {
  return [branch?.address_line_1, branch?.address_line_2, branch?.city, branch?.region, branch?.country]
    .filter(Boolean)
    .join(", ");
}

function isSelectablePharmacyBranch(branch = {}) {
  const name = String(branch?.branch_name || branch?.pharmacy_name || "").trim();
  const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  const country = String(branch?.country || "").trim();
  const locationContext = [branch?.address_line_1, branch?.city, branch?.region]
    .some((value) => String(value || "").trim());

  return Boolean(name && normalizedName !== "na" && country && locationContext);
}

function displayPharmacyBranchName(branch = {}) {
  const name = String(
    typeof branch === "string" ? branch : branch?.branch_name || branch?.pharmacy_name || "",
  ).trim();
  return /^afrimed pharmacy$/i.test(name) ? "AfriMed™ Pharmacy" : name;
}

function buildPharmacyMapHref(branch = {}) {
  const latitude = Number(branch?.latitude);
  const longitude = Number(branch?.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
  }

  const location = formatPharmacyBranchLocation(branch);
  return branch?.address_line_1 && branch?.country && location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
    : "";
}

function formatDistanceKm(value, t) {
  const distance = Number(value);
  if (!Number.isFinite(distance)) return "";
  if (distance < 1) {
    return t("prescriptions.fulfillment.distanceMeters", "{count} m away", {
      count: Math.max(1, Math.round(distance * 1000)),
    });
  }
  return t("prescriptions.fulfillment.distanceKm", "{count} km away", {
    count: distance.toFixed(distance >= 10 ? 0 : 1),
  });
}

function readCachedPrescriptionView(prescriptionId) {
  if (typeof window === "undefined") return null;

  const cacheKey = String(prescriptionId || "").trim();
  if (!cacheKey) return null;

  try {
    const raw = window.sessionStorage.getItem(`afrimed:rx:details:v1:${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedPrescriptionView(prescriptionId, value) {
  if (typeof window === "undefined") return;

  const cacheKey = String(prescriptionId || "").trim();
  if (!cacheKey || !value || typeof value !== "object") return;

  try {
    window.sessionStorage.setItem(`afrimed:rx:details:v1:${cacheKey}`, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

export default function PatientPrescriptionDetails() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation(); // kept (you may use later)
  const { t, languageCode } = useLanguage();
  const routeStateRef = React.useRef(
    location.state && typeof location.state === "object" ? location.state : null
  );
  const routeState = routeStateRef.current;
  const restoredState = React.useMemo(() => {
    return routeState?.restored || routeState || null;
  }, [routeState]);
  const routePatient = React.useMemo(() => {
    return routeState?.patient || restoredState?.patient || null;
  }, [restoredState, routeState]);
  const routeFacility = React.useMemo(() => {
    return restoredState?.facility || routeState?.facility || null;
  }, [restoredState, routeState]);
  const actorRole = React.useMemo(
    () => String(routeState?.actorRole || "patient").trim().toLowerCase(),
    [routeState]
  );

  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrSrc, setQrSrc] = React.useState(null);
  const [viewerUserId, setViewerUserId] = React.useState("");
  const [refundBusy, setRefundBusy] = React.useState(false);
  const [pharmacyOptions, setPharmacyOptions] = React.useState([]);
  const [pharmacyOptionsLoading, setPharmacyOptionsLoading] = React.useState(false);
  const [requestBusy, setRequestBusy] = React.useState(false);
  const [selectedPharmacyOwnerUserId, setSelectedPharmacyOwnerUserId] = React.useState("");
  const [requestedFulfillmentMode, setRequestedFulfillmentMode] = React.useState("");
  const [patientGeo, setPatientGeo] = React.useState({
    status: "idle",
    lat: null,
    lng: null,
  });

  // ✅ print-only target
  const printRef = React.useRef(null);
  const pdfCache = React.useRef(null);
  const pdfFrames = React.useRef([]);
  React.useEffect(() => () => { for (const frame of pdfFrames.current) { URL.revokeObjectURL(frame.dataset.pdfUrl); frame.remove(); } }, []);

  // stub data (replace later with real prescription record)
  // -----------------------------
// DB hydration (REAL)
// -----------------------------
const [rx, setRx] = React.useState(() =>
  buildLocalizedPrescriptionView(readCachedPrescriptionView(id), t, languageCode)
);
const [rxLoading, setRxLoading] = React.useState(() => !readCachedPrescriptionView(id));
const [rxError, setRxError] = React.useState("");
const isDoctorActor = React.useMemo(() => {
  if (actorRole === "doctor") return true;
  const viewer = String(viewerUserId || "").trim();
  if (!viewer) return false;
  return buildIdCandidates(
    rx?.doctor?.userId,
    rx?.doctor?.authUserId,
    rx?.doctorUserId,
  ).includes(viewer);
}, [actorRole, rx?.doctor?.authUserId, rx?.doctor?.userId, rx?.doctorUserId, viewerUserId]);

React.useEffect(() => {
  setRx((current) => buildLocalizedPrescriptionView(current, t, languageCode));
}, [languageCode, t]);

React.useEffect(() => {
  let alive = true;

  (async () => {
    setRxLoading(true);
    setRxError("");

    try {
      // ✅ must be logged in for details screen (verify screen is separate)
      const { data: sess } = await supabase.auth.getSession();
        const userId = sess?.session?.user?.id;
        if (alive) setViewerUserId(String(userId || "").trim());

        if (!userId) {
          throw new Error(
            t("prescriptions.common.signInToView", "Please sign in to view this prescription."),
          );
        }

      // 1) Prescription row
      const { data: p, error: pErr } = await supabase
        .from("prescriptions")
        .select(
          `
          id,
          doctor_user_id,
          patient_user_id,
          appointment_id,
          consultation_id,
          status,
          issued_at,
          expires_at,
          diagnosis,
          notes,
          external_rx_id,
          meta,
          signature_image,
          digital_seal,
          signed_at
        `
        )
        .eq("id", id)
        .maybeSingle();

      if (pErr) throw pErr;
      if (!p) throw new Error(t("prescriptions.common.notFound", "Prescription not found."));

      const meta = p.meta || {};
      const { data: latestFulfillmentRow, error: latestFulfillmentError } = await supabase
        .from("prescription_fulfillments")
        .select("id,prescription_id,pharmacy_id,conversation_id,status,price,created_at,updated_at")
        .eq("prescription_id", p.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (
        latestFulfillmentError &&
        !["PGRST205", "42P01", "42501"].includes(String(latestFulfillmentError?.code || ""))
      ) {
        throw latestFulfillmentError;
      }

      let fulfillmentPharmacyProfile = null;
      if (String(latestFulfillmentRow?.pharmacy_id || "").trim()) {
        const { data: pharmacyProfile, error: pharmacyProfileError } = await supabase
          .from("pharmacy_connect_profiles")
          .select("user_id, pharmacy_name, trading_name, country")
          .eq("user_id", String(latestFulfillmentRow.pharmacy_id).trim())
          .maybeSingle();

        if (
          pharmacyProfileError &&
          !["PGRST205", "42P01", "42501"].includes(String(pharmacyProfileError?.code || ""))
        ) {
          throw pharmacyProfileError;
        }
        fulfillmentPharmacyProfile = pharmacyProfile || null;
      }

      // 2) Resolve doctor / patient ownership ids to auth user ids when needed.
      const [docProfileRes, patientRecordRes] = await Promise.all([
        supabase
          .from("doctor_profiles")
          .select("id, user_id, full_name, register_number, medical_license_number")
          .in("id", buildIdCandidates(p.doctor_user_id, meta.doctorProfileId))
          .maybeSingle(),
        supabase
          .from("patients")
          .select("id, user_id")
          .in("id", buildIdCandidates(p.patient_user_id, meta.patientRecordId))
          .maybeSingle(),
      ]);

      const doctorProfile = docProfileRes?.data || null;
      const patientRecord = patientRecordRes?.data || null;
      const doctorAuthUserId =
        String(
          meta.doctorAuthUserId ||
            doctorProfile?.user_id ||
            p.doctor_user_id ||
            "",
        ).trim();
      const patientAuthUserId =
        String(
          meta.patientAuthUserId ||
            patientRecord?.user_id ||
            p.patient_user_id ||
            "",
        ).trim();

      // ✅ read boundary: only doctor or patient on this prescription can view details
      const allowed = buildIdCandidates(
        p.doctor_user_id,
        p.patient_user_id,
        doctorAuthUserId,
        patientAuthUserId,
      ).includes(userId);
      if (!allowed) {
        throw new Error(
          t("prescriptions.common.accessDenied", "You do not have access to this prescription."),
        );
      }

      // 3) Doctor + patient profiles (display_name lives in profiles)
      const appointmentRes = p.appointment_id
        ? await supabase
            .from("appointments")
            .select("id, practice_id, doctor_id")
            .eq("id", p.appointment_id)
            .maybeSingle()
        : { data: null, error: null };

      if (appointmentRes?.error) throw appointmentRes.error;

      const appointmentRow = appointmentRes?.data || null;
      const consultancyQuery = supabase
        .from("doctor_consultancies")
        .select(
          "id, doctor_id, facility_name, facility_address, facility_city, facility_country, is_primary, sort_order, updated_at"
        );
      const legacyLocationQuery = supabase
        .from("doctor_consulting_locations")
        .select("id, doctor_id, facility_name, street_address, city, country, updated_at");

      const [docRes, patRes, patientProfileRes, directoryDoctorRes, consultancyRes, legacyLocationRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, display_name, full_name, first_name, last_name, email, phone")
          .eq("user_id", doctorAuthUserId)
          .maybeSingle(),
        supabase
          .from("profiles")
          .select("id, user_id, display_name, full_name, first_name, last_name, email, phone")
          .eq("user_id", patientAuthUserId)
          .maybeSingle(),
        supabase
          .from("patient_profiles")
          .select("user_id, full_name, first_name, last_name, email, phone, date_of_birth, national_id_number, national_id, street_address, city, country")
          .eq("user_id", patientAuthUserId)
          .maybeSingle(),
        supabase
          .from("v_doctor_directory_browse")
          .select("doctor_user_id, facility_name, facility_address, facility_city, facility_country")
          .eq("doctor_user_id", doctorAuthUserId)
          .maybeSingle(),
        appointmentRow?.practice_id
          ? consultancyQuery.eq("id", appointmentRow.practice_id).maybeSingle()
          : consultancyQuery
              .eq("doctor_id", doctorAuthUserId)
              .order("is_primary", { ascending: false })
              .order("sort_order", { ascending: true })
              .order("updated_at", { ascending: false })
              .limit(1)
              .maybeSingle(),
        legacyLocationQuery
          .eq("doctor_id", doctorAuthUserId)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const directoryDoctor = directoryDoctorRes?.data || null;
      const consultancy = consultancyRes?.data || null;
      const legacyLocation = legacyLocationRes?.data || null;
      const patientFullName = firstText(
        meta.patientName,
        patRes.data?.display_name,
        patRes.data?.full_name,
        [patRes.data?.first_name, patRes.data?.last_name].filter(Boolean).join(" "),
        patientProfileRes.data?.full_name,
        [patientProfileRes.data?.first_name, patientProfileRes.data?.last_name].filter(Boolean).join(" "),
        routePatient?.full_name,
        routePatient?.display_name,
        routePatient?.name,
        restoredState?.patientName,
      );
      const facilityAddressFallback = resolveFacilityAddress({
        addressCandidates: [
          consultancy?.facility_address,
          directoryDoctor?.facility_address,
          legacyLocation?.street_address,
          routeFacility?.address,
          restoredState?.facilityAddress,
          restoredState?.address,
        ],
        postalCodeCandidates: [
          meta.facilityPostalCode,
          meta.facilityPostCode,
          routeFacility?.postal_code,
          routeFacility?.postalCode,
          routeFacility?.zip_code,
          routeFacility?.zipCode,
          restoredState?.facilityPostalCode,
          restoredState?.facilityPostCode,
          restoredState?.postal_code,
          restoredState?.postalCode,
          restoredState?.zip_code,
          restoredState?.zipCode,
        ],
        cityCandidates: [
          meta.facilityCity,
          consultancy?.facility_city,
          directoryDoctor?.facility_city,
          legacyLocation?.city,
          routeFacility?.city,
          restoredState?.facilityCity,
        ],
        countryCandidates: [
          meta.facilityCountry,
          consultancy?.facility_country,
          directoryDoctor?.facility_country,
          legacyLocation?.country,
          routeFacility?.country,
          restoredState?.facilityCountry,
        ],
      });

      // 4) Assemble an rx object shaped like your UI expects
      //    (Most medication specifics live in prescriptions.meta)
      const rawDoctorName =
        selectDoctorDisplayName(meta, doctorProfile, docRes?.data) ||
        t("prescriptions.common.prescriber", "Prescriber");
      const rawDoctorPrescriptionName =
        selectDoctorPrescriptionName(meta, doctorProfile, docRes?.data) || rawDoctorName;
      const isDoctorViewerForPrescription =
        actorRole === "doctor" ||
        buildIdCandidates(
          p.doctor_user_id,
          doctorAuthUserId,
          meta.doctorAuthUserId,
          doctorProfile?.user_id,
        ).includes(userId);
      const doctorName = isDoctorViewerForPrescription
        ? rawDoctorName
        : resolvePatientFacingDoctorName(rawDoctorName);
      const doctorPrescriptionName = isDoctorViewerForPrescription
        ? rawDoctorPrescriptionName
        : resolvePatientFacingDoctorName(rawDoctorPrescriptionName || rawDoctorName);
      const doctorRegNo = selectDoctorLicenseNumber(meta, doctorProfile, docRes?.data);

      const assembledSchedule = {
        frequencyType: meta.frequencyType || "",
        everyHours: meta.everyHours || null,
        specificDays: Array.isArray(meta.specificDays) ? meta.specificDays : [],
        cycleUseDays: meta.cycleUseDays || null,
        cyclePauseDays: meta.cyclePauseDays || null,
        startDateIso: meta.startsOn || null,
        endDateIso: meta.endsOn || null,
        dosesPerTime: meta.dosesPerTime || "",
        doseUnitLabel: meta.doseUnitLabel || "",
      };
      const medicationEntries =
        Array.isArray(meta.medications) && meta.medications.length
          ? meta.medications
          : [
              {
                id: meta.rxNo || p.id,
                name:
                  meta.medicineName ||
                  meta.medicine ||
                  meta.name ||
                  t("prescriptions.common.medication", "Medication"),
                form: meta.form || meta.dose_form || meta.strength || "",
                composition: meta.ingredients || meta.activeIngredients || meta.composition || "",
                strengthValue: meta.strengthValue || "",
                strengthUnit: meta.strengthUnit || "",
                strengthLabel: meta.strengthLabel || "",
                dosageLabel: meta.dosageLabel || meta.dosage || "",
                frequencyLabel: meta.frequencyLabel || meta.frequency || "—",
                frequencyType: meta.frequencyType || "",
                schedule: assembledSchedule,
                startsOn: meta.startsOn || "",
                endsOn: meta.endsOn || "",
                note: meta.note || "",
              },
            ];
      const primaryMedication = medicationEntries[0] || {};

      const assembled = {
        id: p.id,
        rxNo: prescriptionIdentifier(p),

        medicineName:
          primaryMedication.name ||
          meta.medicineName ||
          meta.medicine ||
          meta.name ||
          t("prescriptions.common.medication", "Medication"),
        form: localizeMedicationFormLabel(
          primaryMedication.form || meta.form || meta.dose_form || meta.strength || "",
          t,
        ),
        ingredients:
          primaryMedication.composition ||
          meta.ingredients ||
          meta.activeIngredients ||
          meta.composition ||
          "",
        medications: medicationEntries,

        // schedule labels (prefer meta, fallback to legacy)
        schedule: assembledSchedule,
        frequencyLabel: meta.frequencyLabel || meta.frequency || "—",
        dosageLabel:
          buildPrescriptionDosageLabel({
            dosageLabel: meta.dosageLabel || meta.dosage || "",
            strengthValue: meta.strengthValue || "",
            strengthUnit: meta.strengthUnit || "",
            strengthLabel: meta.strengthLabel || meta.form || "",
            dosesPerTime: meta.dosesPerTime || "",
            doseUnitLabel: meta.doseUnitLabel || "",
            schedule: assembledSchedule,
          }, { t, languageCode }) || "—",

        // dates (prefer meta if stored, else issued/expires)
        startsOn: meta.startsOn ? parseISO(meta.startsOn) : (p.issued_at ? new Date(p.issued_at) : null),
        endsOn: meta.endsOn ? parseISO(meta.endsOn) : (p.expires_at ? new Date(p.expires_at) : null),

        status: p.status,
        issuedAt: p.issued_at ? new Date(p.issued_at) : null,
        expiresAt: p.expires_at ? new Date(p.expires_at) : null,

        // patient + doctor blocks
        patient: {
          name: patientFullName || t("prescriptions.common.patient", "Patient"),
          careSubjectType: firstText(meta.careSubjectType, "self").toLowerCase(),
          guardianName: firstText(meta.guardianName),
          responsibleRelationship: firstText(meta.responsibleRelationship, meta.care_subject_relationship),
          phone:
            patientProfileRes.data?.phone ||
            patRes.data?.phone ||
            meta.patientPhone ||
            routePatient?.phone ||
            "",
          email:
            patientProfileRes.data?.email ||
            patRes.data?.email ||
            meta.patientEmail ||
            routePatient?.email ||
            "",
          address:
            firstText(
              patientProfileRes.data?.street_address,
              [
                patientProfileRes.data?.city,
                patientProfileRes.data?.country,
              ]
                .filter(Boolean)
                .join(", "),
            ) ||
            meta.patientAddress ||
            routePatient?.address ||
            restoredState?.patientAddress ||
            restoredState?.address ||
            "—",
          addressLines: buildAddressLines(
            patientProfileRes.data?.street_address,
            patientProfileRes.data?.address_line_2,
            [
              patientProfileRes.data?.city,
              patientProfileRes.data?.region,
              patientProfileRes.data?.state,
              patientProfileRes.data?.postal_code,
            ]
              .filter(Boolean)
              .join(", "),
            patientProfileRes.data?.country,
            meta.patientAddressLine1,
            meta.patientAddressLine2,
            [
              meta.patientCity,
              meta.patientRegion,
              meta.patientState,
              meta.patientPostalCode,
            ]
              .filter(Boolean)
              .join(", "),
            meta.patientCountry,
            meta.patientAddress,
            routePatient?.address,
            restoredState?.patientAddress,
            restoredState?.address,
          ),
          country:
            patientProfileRes.data?.country ||
            meta.patientCountry ||
            routePatient?.country ||
            restoredState?.patientCountry ||
            "",
          dob:
            meta.patientDob ||
            patientProfileRes.data?.date_of_birth ||
            routePatient?.date_of_birth ||
            routePatient?.dob ||
            restoredState?.date_of_birth ||
            "",
          nationalId:
            meta.patientNationalId ||
            patientProfileRes.data?.national_id_number ||
            patientProfileRes.data?.national_id ||
            routePatient?.national_id_number ||
            routePatient?.national_id ||
            restoredState?.national_id_number ||
            restoredState?.national_id ||
            "",
        },

        doctor: {
          userId: doctorAuthUserId,
          authUserId: doctorAuthUserId,
          name: doctorName,
          prescriptionName: doctorPrescriptionName,
          regNo: doctorRegNo,
          signatureSrc: p.signature_image || null,
          facility: {
            name:
              meta.facilityName ||
              restoredState?.facilityName ||
              routeFacility?.name ||
              consultancy?.facility_name ||
              directoryDoctor?.facility_name ||
              legacyLocation?.facility_name ||
              restoredState?.hospital ||
              restoredState?.practiceName ||
              t("prescriptions.common.facility", "Facility"),
            address:
              resolveFacilityAddress({
                addressCandidates: [
                  meta.facilityAddress,
                  routeFacility?.address,
                  restoredState?.facilityAddress,
                  restoredState?.address,
                ],
                postalCodeCandidates: [
                  meta.facilityPostalCode,
                  meta.facilityPostCode,
                  routeFacility?.postal_code,
                  routeFacility?.postalCode,
                  routeFacility?.zip_code,
                  routeFacility?.zipCode,
                  restoredState?.facilityPostalCode,
                  restoredState?.facilityPostCode,
                  restoredState?.postal_code,
                  restoredState?.postalCode,
                  restoredState?.zip_code,
                  restoredState?.zipCode,
                ],
                cityCandidates: [
                  meta.facilityCity,
                  routeFacility?.city,
                  restoredState?.facilityCity,
                ],
                countryCandidates: [
                  meta.facilityCountry,
                  routeFacility?.country,
                  restoredState?.facilityCountry,
                ],
              }) ||
              facilityAddressFallback ||
              "—",
            phone:
              meta.facilityPhone ||
              routeFacility?.phone ||
              restoredState?.facilityPhone ||
              "",
            email:
              meta.facilityEmail ||
              routeFacility?.email ||
              restoredState?.facilityEmail ||
              "",
          },
        },

        // signature snapshot on the prescription (future: show if present)
        signature_image: p.signature_image || null,
        signed_at: p.signed_at || null,
        digital_seal: p.digital_seal || null,

        issuedVia: meta.issuedVia || "AfriMed™",
        pharmacyPayment: {
          pharmacyName: firstText(
            meta.pharmacy_branch_name,
            meta.pharmacy_name,
            meta.pharmacyName,
          ),
          pharmacyOwnerUserId: firstText(meta.pharmacy_owner_user_id),
          fulfillmentMode: firstText(meta.fulfillment_mode, "pickup").toLowerCase(),
          paymentStatus: firstText(meta.payment_status, "pending").toLowerCase(),
          refundStatus: firstText(meta.stripe_refund_status).toLowerCase(),
          medicineSubtotalCents: Number(meta.medicine_subtotal_cents || 0),
          deliveryFeeCents: Number(meta.delivery_fee_cents || 0),
          currency: resolvePaymentCurrency({
            explicitCurrency: meta.currency,
            sponsorCountry:
              meta.sponsor_country ||
              meta.sponsorCountry ||
              meta.payer_market ||
              meta.payerMarket,
            sponsorDialCode: meta.sponsor_dial_code || meta.sponsorDialCode,
            country:
              meta.pharmacy_country ||
              meta.pharmacyCountry ||
              meta.patient_country ||
              meta.patientCountry,
            fallbackCurrency: "ZAR",
          }),
          quotedAt: firstText(meta.quoted_at, meta.pharmacy_claimed_at),
          paymentIntentId: firstText(meta.payment_intent_id),
        },
        pharmacyRequest: {
          requestState: firstText(meta.pharmacy_request_state).toLowerCase(),
          requestedPharmacyOwnerUserId: firstText(
            meta.requested_pharmacy_owner_user_id,
            meta.pharmacy_requested_owner_user_id,
          ),
          requestedPharmacyName: firstText(
            meta.requested_pharmacy_branch_name,
            meta.requested_pharmacy_name,
          ),
          requestedFulfillmentMode: firstText(meta.requested_fulfillment_mode).toLowerCase(),
          requestedAt: firstText(meta.pharmacy_requested_at),
        },
        fulfillment: latestFulfillmentRow?.id
          ? {
              id: String(latestFulfillmentRow.id || "").trim(),
              pharmacyId: String(latestFulfillmentRow.pharmacy_id || "").trim(),
              pharmacyName: firstText(
                fulfillmentPharmacyProfile?.pharmacy_name,
                fulfillmentPharmacyProfile?.trading_name,
                meta.requested_pharmacy_branch_name,
                meta.requested_pharmacy_name,
              ),
              status: firstText(latestFulfillmentRow.status, "issued").toLowerCase(),
              price: Number(latestFulfillmentRow.price || 0),
              conversationId: firstText(
                latestFulfillmentRow.conversation_id,
                meta.fulfillment_support_conversation_id,
              ),
              createdAt: firstText(latestFulfillmentRow.created_at),
              updatedAt: firstText(latestFulfillmentRow.updated_at),
            }
          : null,
        supportConversationId: firstText(
          latestFulfillmentRow?.conversation_id,
          meta.fulfillment_support_conversation_id,
        ),
      };

      const localizedView = buildLocalizedPrescriptionView(assembled, t, languageCode);

      if (!alive) return;
      setRx(localizedView);
      writeCachedPrescriptionView(p.id, localizedView);
    } catch (e) {
      if (!alive) return;
      if (!readCachedPrescriptionView(id)) {
        setRx(null);
      }
      setRxError(
        e?.message || t("prescriptions.common.loadFailed", "Failed to load prescription."),
      );
    } finally {
      if (!alive) return;
      setRxLoading(false);
    }
  })();

  return () => {
    alive = false;
  };
}, [id, routeState]);

  const qrValue = React.useMemo(() => {
    const rxId = rx?.id || id;
    return buildPrescriptionVerifyUrl(rxId, window.location.origin);
  }, [id, rx?.id]);
  const verifyPath = React.useMemo(() => buildPrescriptionVerifyPath(rx?.id || id), [rx?.id, id]);
  const verifyState = React.useMemo(
    () =>
      buildPrescriptionVerifyState({
        from: "patient-prescription-details",
        actorRole: location.state?.actorRole || "patient",
        prescriptionId: rx?.id || id,
        returnTo: location.pathname,
        returnState: location.state || null,
        snapshot: rx ? buildPrescriptionVerifySnapshot(rx) : null,
      }),
    [id, location.pathname, location.state, rx]
  );

  React.useEffect(() => {
    let alive = true;
    setQrSrc(null);

    if (!qrValue) {
      return () => {
        alive = false;
      };
    }

    makeBrandedQrDataUrl(qrValue, {
      iconSrc: abs(QR_ELEPHANT),
    })
      .then((url) => {
        if (alive) setQrSrc(url);
      })
      .catch(() => {
        if (alive) setQrSrc(null);
      });

    return () => {
      alive = false;
    };
  }, [qrValue]);

  // Facility name used for browser print header title (when headers enabled)
  const facilityName =
    rx?.doctor?.facility?.name ||
    rx?.doctor?.prescriptionName ||
    rx?.doctor?.name ||
    t("prescriptions.common.title", "Prescription");
  const scheduleRows = React.useMemo(() => {
    const rows = buildPrescriptionScheduleRows(rx || {}, { t, languageCode });
    return rows.length ? rows : [t("prescriptions.wizard.scheduleUnavailable", "Schedule unavailable")];
  }, [languageCode, rx, t]);
  const medicationRows = React.useMemo(() => {
    const list = Array.isArray(rx?.medications) ? rx.medications : [];
    return list.map((item, index) => {
      const rows = buildPrescriptionScheduleRows(item || {}, { t, languageCode });
      return {
        ...item,
        key: item?.id || `${item?.name || "medication"}-${index}`,
        rows: rows.length ? rows : [t("prescriptions.wizard.scheduleUnavailable", "Schedule unavailable")],
      };
    });
  }, [languageCode, rx?.medications, t]);
  const pharmacyPayment = rx?.pharmacyPayment || null;
  const pharmacyRequest = rx?.pharmacyRequest || null;
  const fulfillment = rx?.fulfillment || null;
  const fulfillmentStatus = String(fulfillment?.status || "").trim().toLowerCase();
  const supportConversationId = String(
    rx?.supportConversationId || fulfillment?.conversationId || "",
  ).trim();
  const usingFulfillmentFlow = Boolean(fulfillment?.id || supportConversationId);
  const patientCountry = String(rx?.patient?.country || "").trim();
  const fulfillmentCurrency = resolvePaymentCurrency({
    explicitCurrency: pharmacyPayment?.currency,
    country: patientCountry,
    fallbackCurrency: "ZAR",
  });
  const pharmacyTotalCents =
    Number(pharmacyPayment?.medicineSubtotalCents || 0) + Number(pharmacyPayment?.deliveryFeeCents || 0);
  const pharmacyPaymentStatus = String(pharmacyPayment?.paymentStatus || "").trim().toLowerCase();
  const pharmacyRefundStatus = String(pharmacyPayment?.refundStatus || "").trim().toLowerCase();
  const pharmacyQuoteReady =
    Boolean(String(pharmacyPayment?.pharmacyOwnerUserId || "").trim()) &&
    Number(pharmacyPayment?.medicineSubtotalCents || 0) > 0;
  const pharmacyPaid = pharmacyPaymentStatus === "paid";
  const pharmacyRefundPending =
    pharmacyPaymentStatus === "refund_pending" || pharmacyRefundStatus === "pending";
  const pharmacyRefunded =
    pharmacyPaymentStatus === "refunded" || pharmacyRefundStatus === "succeeded";
  const fulfillmentPaid = fulfillmentStatus === "paid";
  const pharmacyRequestPending =
    String(pharmacyRequest?.requestState || "").trim().toLowerCase() === "patient_requested" &&
    !pharmacyQuoteReady;
  const selectedPharmacyOption = pharmacyOptions.find(
    (branch) => String(branch?.owner_user_id || "").trim() === selectedPharmacyOwnerUserId,
  );
  const deliveryAllowed = Boolean(selectedPharmacyOption?.delivery_enabled);
  const fulfillmentPharmacyName = firstText(
    fulfillment?.pharmacyName,
    pharmacyPayment?.pharmacyName,
    pharmacyRequest?.requestedPharmacyName,
  );
  // The fulfilment row is the assignment authority. A profile read may enrich
  // its name, but must never erase a selected Pharmacy from Patient truth.
  const pharmacySelectionProven = hasCanonicalPharmacyAssignment(fulfillment);
  const selectionLocked = pharmacySelectionProven && usingFulfillmentFlow
    ? ["pharmacy_selected", "preparing", "awaiting_payment", "paid", "ready_for_pickup"].includes(fulfillmentStatus)
    : pharmacyQuoteReady;
  const presentationPharmacyName = pharmacySelectionProven ? fulfillmentPharmacyName : "";
  const fulfillmentMode =
    pharmacySelectionProven
      ? pharmacyPayment?.fulfillmentMode || pharmacyRequest?.requestedFulfillmentMode || requestedFulfillmentMode
      : "";
  const canonicalFulfillmentState = resolveCanonicalFulfillmentState({
    status: fulfillmentStatus,
    mode: fulfillmentMode,
  });
  const fulfillmentIsDelivery = canonicalFulfillmentState.mode === "delivery";
  const fulfillmentModeLabel = pharmacySelectionProven
    ? formatFulfillmentModeLabel(fulfillmentMode, t)
    : t("prescriptions.fulfillment.awaitingSelection", "Awaiting selection");
  const terminalFulfillmentReady = pharmacySelectionProven && pharmacyPaid && canonicalFulfillmentState.ready;
  const fulfillmentReadyForPickup = terminalFulfillmentReady;
  const fulfillmentSummary = buildPatientFulfillmentSummary({
    t,
    fulfillmentStatus,
    pharmacyQuoteReady,
    pharmacyPaid,
    pharmacyRefundPending,
    pharmacyRefunded,
    pharmacyRequestPending,
    pharmacyName: presentationPharmacyName,
    fulfillmentMode,
    pharmacySelectionProven,
  });
  const doctorFulfillmentBanner = React.useMemo(() => {
    if (fulfillmentStatus === "ready_for_pickup") {
      if (fulfillmentIsDelivery) {
        return t(
          "prescriptions.fulfillment.doctorBanner.readyForDelivery",
          "{pharmacy} marked this prescription ready for delivery in the patient fulfilment lane.",
          { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") },
        );
      }
      return t(
        "prescriptions.fulfillment.doctorBanner.ready",
        "{pharmacy} marked this prescription ready for pickup in the patient fulfilment lane.",
        { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") }
      );
    }
    if (fulfillmentStatus === "paid") {
      return t(
        fulfillmentIsDelivery
          ? "prescriptions.fulfillment.doctorBanner.paidDelivery"
          : "prescriptions.fulfillment.doctorBanner.paid",
        fulfillmentIsDelivery
          ? "{pharmacy} has confirmed payment and is arranging delivery."
          : "{pharmacy} has confirmed payment and is preparing release for pickup.",
        { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") }
      );
    }
    if (fulfillmentStatus === "awaiting_payment") {
      return t(
        "prescriptions.fulfillment.doctorBanner.awaitingPayment",
        "{pharmacy} prepared a payable quote for the patient.",
        { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") }
      );
    }
    if (fulfillmentStatus === "preparing") {
      return t(
        "prescriptions.fulfillment.doctorBanner.preparing",
        "{pharmacy} accepted the prescription and is preparing it.",
        { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") }
      );
    }
    if (fulfillmentStatus === "pharmacy_selected") {
      return t(
        "prescriptions.fulfillment.doctorBanner.reviewing",
        "{pharmacy} is reviewing this prescription in the patient fulfilment lane.",
        { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") }
      );
    }
    if (fulfillmentStatus === "rejected") {
      return t(
        "prescriptions.fulfillment.doctorBanner.rejected",
        "{pharmacy} could not fulfil this prescription. The patient must choose another pharmacy.",
        { pharmacy: fulfillmentPharmacyName || t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy") }
      );
    }
    if (pharmacyRequestPending) {
      return t(
        "prescriptions.fulfillment.doctorBanner.requestPending",
        "{pharmacy} is preparing a quote for the patient.",
        { pharmacy: pharmacyRequest?.requestedPharmacyName || t("prescriptions.fulfillment.requestedPharmacy", "Requested pharmacy") }
      );
    }
    return t(
      "prescriptions.fulfillment.doctorBanner.notStarted",
      "Patient fulfilment starts when the patient opens this prescription in AfriMed™."
    );
  }, [fulfillmentIsDelivery, fulfillmentPharmacyName, fulfillmentStatus, pharmacyRequest?.requestedPharmacyName, pharmacyRequestPending, t]);
  const doctorFulfillmentNextStep = React.useMemo(() => {
    if (fulfillmentStatus === "ready_for_pickup") {
      if (fulfillmentIsDelivery) {
        return t(
          "prescriptions.fulfillment.doctorNextStep.delivery",
          "The pharmacy is arranging delivery of the prescription.",
        );
      }
      return t(
        "prescriptions.fulfillment.doctorNextStep.ready",
        "Patient can now collect the prescription from the selected pharmacy."
      );
    }
    if (["paid", "awaiting_payment", "preparing", "pharmacy_selected"].includes(fulfillmentStatus)) {
      return t(
        "prescriptions.fulfillment.doctorNextStep.active",
        "Patient and pharmacy continue fulfilment from the patient lane."
      );
    }
    if (fulfillmentStatus === "rejected") {
      return t(
        "prescriptions.fulfillment.doctorNextStep.rejected",
        "Patient needs to choose another signed-up pharmacy in the patient lane."
      );
    }
    return t(
      "prescriptions.fulfillment.doctorNextStep.pending",
      "Waiting for the patient to choose a pharmacy."
    );
  }, [fulfillmentIsDelivery, fulfillmentStatus, t]);
  const fulfillmentSummaryChipClass =
    fulfillmentSummary.tone === "critical"
      ? "bg-[rgba(var(--clr-error),0.10)] text-[rgb(var(--clr-error))]"
      : fulfillmentSummary.tone === "success"
        ? "bg-[rgba(0,189,196,0.12)] text-[#008A8F]"
        : fulfillmentSummary.tone === "info"
          ? "bg-[rgba(24,50,119,0.08)] text-primary"
          : "bg-[rgba(86,106,139,0.08)] text-on-surface";
  const fulfillmentSummaryTextClass =
    fulfillmentSummary.tone === "critical"
      ? "text-[rgb(var(--clr-error))]"
      : fulfillmentSummary.tone === "success"
        ? "text-[#008A8F]"
        : "text-on-surface";

  React.useEffect(() => {
    if (selectionLocked) return undefined;
    if (patientGeo.status !== "idle") return undefined;

    let cancelled = false;
    setPatientGeo((current) => ({ ...current, status: "loading" }));

    nearbyHealthcareService
      .getCurrentLocation()
      .then(({ latitude, longitude, error }) => {
        if (cancelled) return;
        if (error || latitude == null || longitude == null) {
          setPatientGeo({ status: "unavailable", lat: null, lng: null });
          return;
        }
        setPatientGeo({
          status: "granted",
          lat: Number(latitude),
          lng: Number(longitude),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPatientGeo({ status: "unavailable", lat: null, lng: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientGeo.status, selectionLocked]);

  React.useEffect(() => {
    if (selectionLocked) {
      setPharmacyOptions([]);
      setSelectedPharmacyOwnerUserId("");
      return;
    }

    let cancelled = false;
    setPharmacyOptionsLoading(true);

    listPharmacyBranches({
      country: patientCountry || undefined,
      lat: Number.isFinite(patientGeo.lat) ? patientGeo.lat : undefined,
      lng: Number.isFinite(patientGeo.lng) ? patientGeo.lng : undefined,
    })
      .then((data) => {
        if (cancelled) return;
        const nextBranches = (Array.isArray(data?.branches) ? data.branches : []).filter(
          isSelectablePharmacyBranch,
        );
        setPharmacyOptions(nextBranches);
        setSelectedPharmacyOwnerUserId((current) => {
          if (current && nextBranches.some((branch) => String(branch?.owner_user_id || "").trim() === current)) {
            return current;
          }
          if (fulfillmentStatus === "rejected") {
            const fallbackBranch = nextBranches.find(
              (branch) =>
                String(branch?.owner_user_id || "").trim() &&
                String(branch?.owner_user_id || "").trim() !== String(fulfillment?.pharmacyId || "").trim(),
            );
            if (fallbackBranch) {
              return String(fallbackBranch?.owner_user_id || "").trim();
            }
          }
          return String(
            pharmacyRequest?.requestedPharmacyOwnerUserId ||
              nextBranches[0]?.owner_user_id ||
              "",
          ).trim();
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPharmacyOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPharmacyOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    patientCountry,
    patientGeo.lat,
    patientGeo.lng,
    selectionLocked,
    fulfillment?.pharmacyId,
    fulfillmentStatus,
    pharmacyRequest?.requestedPharmacyOwnerUserId,
  ]);

  function selectPharmacyBranch(ownerUserId) {
    const nextOwnerUserId = String(ownerUserId || "").trim();
    if (!nextOwnerUserId || nextOwnerUserId === selectedPharmacyOwnerUserId) return;
    setSelectedPharmacyOwnerUserId(nextOwnerUserId);
    // Mode consent belongs to the exact branch choice. It cannot be inherited
    // from another branch or silently defaulted by availability.
    setRequestedFulfillmentMode("");
  }

  function handleBack() {
    const returnTo = String(location.state?.returnTo || "").trim();
    if (returnTo) {
      nav(returnTo, {
        replace: true,
        state: location.state?.returnState || null,
      });
      return;
    }

    nav("/settings/patient/p/ehr", {
      replace: true,
      state: {
        appointmentId: location.state?.appointmentId || null,
        prescriptionId: location.state?.prescriptionId || id,
      },
    });
  }

  async function canonicalPdf() {
    if (rxLoading || rxError || !qrSrc) throw new Error(t("prescriptions.common.documentPreparing", "The prescription document is still preparing. Please try again."));
    const key = JSON.stringify([rx, qrSrc, languageCode]);
    if (pdfCache.current?.key !== key) {
      pdfCache.current = { key, promise: renderPrescriptionPdf(printRef.current, `${rx.rxNo}.pdf`) };
    }
    try { return await pdfCache.current.promise; }
    catch (error) { pdfCache.current = null; throw error; }
  }
  async function doPrint() {
    try {
      const blob = await canonicalPdf();
      const frame = document.createElement('iframe');
      frame.title = `${rx.rxNo}.pdf`;
      frame.style.cssText = 'position:fixed;width:1px;height:1px;bottom:0;left:0;border:0;';
      document.body.appendChild(frame); pdfFrames.current.push(frame);
      printPrescriptionPdf(blob, frame);
    } catch { window.alert(t("prescriptions.common.documentError", "The prescription PDF could not be prepared. Please try again.")); }
  }
  async function doDownloadPdf() {
    try { downloadPrescriptionPdf(await canonicalPdf(), `${rx.rxNo}.pdf`); }
    catch { window.alert(t("prescriptions.common.documentError", "The prescription PDF could not be prepared. Please try again.")); }
  }

  async function handleCancelPharmacyOrder() {
    if (!rx?.id || refundBusy || !pharmacyPaid) return;
    if (
      typeof window !== "undefined" &&
      !window.confirm("Cancel this pharmacy order and trigger the refund now?")
    ) {
      return;
    }

    setRefundBusy(true);
    setRxError("");

    try {
      const result = await reversePharmacyPayment({
        prescription_id: rx.id,
        reason: "patient_cancelled_pharmacy_order",
      });

      setRx((current) => {
        if (!current) return current;
        return {
          ...current,
          pharmacyPayment: {
            ...(current.pharmacyPayment || {}),
            paymentStatus: String(result?.payment_status || "refund_pending").trim().toLowerCase(),
            refundStatus: String(result?.refund_status || "pending").trim().toLowerCase(),
          },
        };
      });
      window.dispatchEvent(new Event("afrimed:transactions:changed"));
    } catch (error) {
      setRxError(error?.message || "Could not cancel the pharmacy order right now.");
    } finally {
      setRefundBusy(false);
    }
  }

  async function handleRequestPharmacy() {
    if (!rx?.id || requestBusy || !selectedPharmacyOwnerUserId || !requestedFulfillmentMode) return;

    setRequestBusy(true);
    setRxError("");

    try {
      const result = await requestPharmacyFulfillment({
        prescription_id: rx.id,
        requested_pharmacy_owner_user_id: selectedPharmacyOwnerUserId,
        fulfillment_mode: requestedFulfillmentMode,
        lat: Number.isFinite(patientGeo.lat) ? patientGeo.lat : undefined,
        lng: Number.isFinite(patientGeo.lng) ? patientGeo.lng : undefined,
      });

      setRx((current) => {
        if (!current) return current;
        return {
          ...current,
          supportConversationId: String(result?.conversation_id || current.supportConversationId || "").trim(),
          fulfillment: {
            id: String(result?.fulfillment_id || "").trim(),
            pharmacyId: String(
              result?.requested_pharmacy_owner_user_id || selectedPharmacyOwnerUserId,
            ).trim(),
            pharmacyName: String(
              result?.requested_pharmacy_branch_name ||
                result?.requested_pharmacy_name ||
                selectedPharmacyOption?.branch_name ||
                selectedPharmacyOption?.pharmacy_name ||
                "",
            ).trim(),
            status: String(result?.pharmacy_request_state || "pharmacy_selected").trim().toLowerCase(),
            price: 0,
            conversationId: String(result?.conversation_id || "").trim(),
            createdAt: String(result?.requested_at || new Date().toISOString()).trim(),
            updatedAt: String(result?.requested_at || new Date().toISOString()).trim(),
          },
          pharmacyRequest: {
            requestState: String(result?.pharmacy_request_state || "pharmacy_selected")
              .trim()
              .toLowerCase(),
            requestedPharmacyOwnerUserId: String(
              result?.requested_pharmacy_owner_user_id || selectedPharmacyOwnerUserId,
            ).trim(),
            requestedPharmacyName: String(
              result?.requested_pharmacy_branch_name ||
                result?.requested_pharmacy_name ||
                selectedPharmacyOption?.branch_name ||
                selectedPharmacyOption?.pharmacy_name ||
                "",
            ).trim(),
            requestedFulfillmentMode: String(
              result?.requested_fulfillment_mode || requestedFulfillmentMode,
            )
              .trim()
              .toLowerCase(),
            requestedAt: String(result?.requested_at || new Date().toISOString()).trim(),
          },
        };
      });
    } catch (error) {
      setRxError(
        error?.message ||
          t(
            "prescriptions.fulfillment.errors.request",
            "Could not request pharmacy fulfilment right now.",
          ),
      );
    } finally {
      setRequestBusy(false);
    }
  }

  function openSupportThread() {
    if (!supportConversationId) return;
    nav(`/messages/${supportConversationId}`, {
      state: {
        conversationTitle: t("prescriptions.fulfillment.openThreadShort", "Fola"),
        conversationType: "fola",
        returnTo: location.pathname,
        returnToState: location.state || null,
      },
    });
  }

  function openFolaFulfillmentAssistant() {
    nav("/messages/fola", {
      state: {
        conversationTitle: t("prescriptions.fulfillment.openThreadShort", "Fola"),
        conversationType: "fola",
        prescriptionId: rx?.id || null,
        fulfillmentContext: "pharmacy_selection",
        returnTo: location.pathname,
        returnToState: location.state || null,
      },
    });
  }

  async function waitForImages(rootEl) {
    const imgs = Array.from(rootEl.querySelectorAll("img"));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => {
              img.onload = res;
              img.onerror = res; // don't block forever
            })
      )
    );
  }

// -----------------------------
// UI guards (AfriMed clean)
// -----------------------------
if (rxLoading && !rx) {
  return (
      <div className="w-full" data-hide-tabbar="true">
      <div className="no-print afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <div className="animate-pulse">
          <div className="h-6 w-40 rounded bg-[rgba(2,6,23,0.08)]" />
          <div className="mt-3 h-4 w-64 rounded bg-[rgba(2,6,23,0.08)]" />
          <div className="mt-8 h-48 w-full rounded-[12px] bg-[rgba(2,6,23,0.06)]" />
        </div>
      </div>
    </div>
  );
}

if (!rx && rxError) {
  return (
      <div className="w-full" data-hide-tabbar="true">
      <div className="no-print afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarRx title={t("prescriptions.common.title", "Prescription")} onBack={handleBack} />
        <div className="mt-6 text-body-medium text-on-surface-var">
          {rxError || t("prescriptions.common.notFound", "Prescription not found.")}
        </div>
      </div>
    </div>
  );
}

if (!rx) {
  return (
      <div className="w-full" data-hide-tabbar="true">
      <div className="no-print afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarRx title={t("prescriptions.common.title", "Prescription")} onBack={handleBack} />
        <div className="mt-6 text-body-medium text-on-surface-var">
          {t("prescriptions.common.notFound", "Prescription not found.")}
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="w-full" data-hide-tabbar="true">
      {/* SCREEN UI (hidden in print) */}
      <div
        className="no-print afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(128px+env(safe-area-inset-bottom))] md:pb-12"
      >
        <TopBarRx
          title={rx.rxNo}
          meta={rx.medicineName}
          subtitle={[rx.form, rx.ingredients].filter(Boolean).join(" · ")}
          onBack={handleBack}
          right={
            <div className="flex items-center gap-2">
              <ActionIconButton icon={ICON_PRINT} label={t("common.actions.print", "Print")} onClick={doPrint} />
              <ActionIconButton icon={ICON_DOWNLOAD} label={t("common.actions.download", "Download")} onClick={doDownloadPdf} />
              <ActionIconButton icon={ICON_QR} label={t("prescriptions.common.qrCode", "QR code")} onClick={() => setQrOpen(true)} />
            </div>
          }
        />

        <div className="mt-6">
          <div className="text-title-medium text-on-surface">
            {t("prescriptions.common.patient", "Patient")}
          </div>
          <div className="mt-1 text-body-medium text-on-surface">{rx.patient?.name || "—"}</div>
          {rx.patient?.careSubjectType === "dependant" && rx.patient?.guardianName ? (
            <div className="mt-1 text-body-small text-on-surface-var">
              {t("prescriptions.common.responsibleAdult", "Responsible adult")}:{" "}
              <span className="text-on-surface">
                {rx.patient.guardianName}
                {rx.patient?.responsibleRelationship
                  ? ` • ${rx.patient.responsibleRelationship}`
                  : ""}
              </span>
            </div>
          ) : null}
          {Array.isArray(rx.patient?.addressLines) && rx.patient.addressLines.length
            ? rx.patient.addressLines.map((line, index) => (
                <div key={`${line}-${index}`} className="mt-1 text-body-small text-on-surface-var">
                  {line}
                </div>
              ))
            : rx.patient?.address && rx.patient.address !== "—"
              ? (
                  <div className="mt-1 text-body-small text-on-surface-var">{rx.patient.address}</div>
                )
              : null}
        </div>

        {/* Schedule group */}
        <Card className="print-paper mt-6 overflow-hidden">
          <div className="px-4 pb-2 pt-4 text-title-medium text-on-surface-var">
            {t("prescriptions.wizard.schedule", "Schedule")}
          </div>

          {medicationRows.length > 1 ? (
            <div>
              {medicationRows.map((item, itemIndex) => (
                <div key={item.key}>
                  <div className="px-4 pt-4">
                    <div className="text-title-medium text-on-surface">{item.name || t("prescriptions.common.medication", "Medication")}</div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {[item.form, item.composition].filter(Boolean).join(" • ")}
                    </div>
                  </div>
                  <div className="mt-2">
                    <ScheduleSummaryGrid source={item} />
                  </div>
                  {item.note ? (
                    <div className="px-4 pb-4 text-body-medium text-on-surface whitespace-pre-wrap">
                      {item.note}
                    </div>
                  ) : null}
                  {itemIndex < medicationRows.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
                </div>
              ))}
            </div>
          ) : (
            <div>
              <ScheduleSummaryGrid source={rx} />
            </div>
          )}

          {/* Prescriber block */}
          <div className="px-4 pb-4 pt-6">
            <div className="text-body-small text-on-surface-var">
              {t("prescriptions.common.prescriber", "Prescriber")}
            </div>
            <div className="mt-1 text-body-medium text-on-surface">{rx.doctor.prescriptionName || rx.doctor.name}</div>
            <div className="mt-1 text-body-small text-on-surface-var">
              {t("prescriptions.common.registration", "Registration")}:{" "}
              <span className="text-on-surface">{rx.doctor.regNo}</span>
            </div>

            {/* ✅ On-screen dotted line WITH assets sitting ON the line (blueprint) */}
            <SignatureStampLine
              sigSrc={rx.doctor.signatureSrc || SIG_PLACEHOLDER}
              stampBaseSrc={abs(DR_STAMP_BASE)}
              doctorName={rx.doctor.prescriptionName || rx.doctor.name}
              regNo={rx.doctor.regNo}
              sigClassName="h-[68px] w-[132px]"
              stampSizePx={70}
              sigNudgeY={-4}
              stampNudgeY={-4}
            />

            <div className="mt-4 text-body-small text-on-surface-var">
              {t(
                "prescriptions.common.poweredByIssuedVia",
                "Powered by {issuedVia}. The prescriber remains responsible for clinical decisions.",
                { issuedVia: rx.issuedVia },
              )}
            </div>
          </div>
        </Card>

        <Card className="mt-6 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-title-medium text-on-surface">
                {t("prescriptions.fulfillment.title", "Pharmacy fulfilment")}
              </div>
              {!fulfillmentReadyForPickup ? (
                <div className="mt-1 text-body-medium text-on-surface-var">
                  {isDoctorActor
                  ? doctorFulfillmentBanner
                  : usingFulfillmentFlow
                  ? fulfillmentReadyForPickup
                    ? fulfillmentIsDelivery
                      ? t(
                          "prescriptions.fulfillment.banner.readyForDelivery",
                          "{pharmacy} marked this prescription as ready for delivery.",
                          { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                        )
                      : t(
                          "prescriptions.fulfillment.banner.ready",
                          "{pharmacy} marked this prescription as ready for pickup.",
                          { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                        )
                    : fulfillmentPaid
                      ? t(
                          fulfillmentIsDelivery
                            ? "prescriptions.fulfillment.banner.paidDelivery"
                            : "prescriptions.fulfillment.banner.paid",
                          fulfillmentIsDelivery
                            ? "Payment is confirmed. {pharmacy} is arranging delivery."
                            : "Payment is confirmed. {pharmacy} is preparing release for pickup.",
                          { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                        )
                      : fulfillmentStatus === "awaiting_payment"
                    ? t(
                        "prescriptions.fulfillment.banner.awaitingPayment",
                        "{pharmacy} has set a price for this prescription.",
                        { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                      )
                    : fulfillmentStatus === "preparing"
                      ? t(
                          "prescriptions.fulfillment.banner.preparing",
                          "{pharmacy} is preparing this prescription.",
                          { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                        )
                      : fulfillmentStatus === "pharmacy_selected"
                        ? t(
                            "prescriptions.fulfillment.banner.reviewing",
                            "{pharmacy} is reviewing this prescription.",
                            { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                          )
                        : fulfillmentStatus === "rejected"
                          ? t(
                              "prescriptions.fulfillment.banner.rejected",
                              "{pharmacy} could not fulfil this prescription.",
                              { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.theSelectedPharmacy", "The selected pharmacy") },
                            )
                          : t(
                              "prescriptions.fulfillment.banner.chooseBranch",
                              "Choose a signed-up pharmacy branch to continue.",
                            )
                  : pharmacyPayment?.pharmacyName
                    ? t(
                        "prescriptions.fulfillment.banner.handling",
                        "{pharmacy} is handling this prescription.",
                        { pharmacy: pharmacyPayment.pharmacyName },
                      )
                    : pharmacyRequestPending
                      ? t(
                          "prescriptions.fulfillment.banner.requestPending",
                          "{pharmacy} is preparing the quote.",
                          { pharmacy: pharmacyRequest?.requestedPharmacyName || t("prescriptions.fulfillment.requestedPharmacy", "Requested pharmacy") },
                        )
                      : t(
                          "prescriptions.fulfillment.banner.chooseBranchForMode",
                          "Choose a signed-up pharmacy branch so this prescription can be sent for pickup or delivery.",
                        )}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-label-small ${fulfillmentSummaryChipClass}`}>
                {fulfillmentReadyForPickup
                  ? t("prescriptions.fulfillment.state.paid", "Paid")
                  : fulfillmentSummary.label}
              </span>
              <span className="rounded-full bg-[rgba(24,50,119,0.08)] px-3 py-1 text-label-small text-on-surface-var">
                {fulfillmentModeLabel}
              </span>
              {fulfillmentReadyForPickup ? (
                <div className="rounded-full bg-[rgba(0,189,196,0.12)] px-3 py-1 text-label-small text-[#008A8F]">
                  {fulfillmentSummary.label}
                </div>
              ) : null}
              {!isDoctorActor ? (
                <ContextFeedbackLauncher
                  section="prescription-fulfillment"
                  roleOverride="patient"
                  entity={{
                    id: rx?.id,
                    label: rx?.rxNo || rx?.medicineName || "prescription",
                    type: "prescription",
                  }}
                  context={{
                    fulfillmentStatus,
                    pharmacyName: fulfillmentPharmacyName,
                    paymentStatus: pharmacyPaymentStatus,
                    requestState: pharmacyRequest?.requestState || "",
                  }}
                  subtitle={t(
                    "prescriptions.fulfillment.feedbackSubtitle",
                    "Capture this prescription fulfilment step so AfriMed™ can reopen the exact tester context.",
                  )}
                />
              ) : null}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3 sm:gap-x-6 xl:grid-cols-4">
            <div className="min-w-0">
              <div className="text-label-large text-on-surface-var">
                {t("prescriptions.fulfillment.currentState", "Current state")}
              </div>
              <div className={`mt-2 text-title-medium ${fulfillmentSummaryTextClass}`}>
                {fulfillmentSummary.label}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-label-large text-on-surface-var">
                {t("prescriptions.fulfillment.pharmacy", "Pharmacy")}
              </div>
              <div className="mt-2 text-title-medium text-on-surface">
                {presentationPharmacyName
                  ? displayPharmacyBranchName(presentationPharmacyName)
                  : t("prescriptions.fulfillment.awaitingSelection", "Awaiting selection")}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-label-large text-on-surface-var">
                {t("prescriptions.fulfillment.mode", "Mode")}
              </div>
              <div className="mt-2 text-title-medium text-on-surface">{fulfillmentModeLabel}</div>
            </div>
            <div className="col-span-full min-w-0 xl:col-auto">
              <div className="text-label-large text-on-surface-var">
                {t("prescriptions.fulfillment.nextStepLabel", "Next step")}
              </div>
              <div className="mt-2 text-body-medium text-on-surface">
                {isDoctorActor ? doctorFulfillmentNextStep : fulfillmentSummary.nextStep}
              </div>
            </div>
          </div>

          <Divider insetStart={0} insetEnd={0} className="mt-4" variant="outlineVariant" />

          <div className="mt-4 text-body-medium text-on-surface">
            {isDoctorActor ? doctorFulfillmentBanner : fulfillmentSummary.description}
          </div>
        </Card>

        <div className="mt-6">
          {!isDoctorActor && supportConversationId ? (
            <div className="mt-4">
              <Button type="button" variant="tonal" onClick={openSupportThread}>
                {t("prescriptions.fulfillment.openThread", "Open Messages thread")}
              </Button>
            </div>
          ) : !isDoctorActor ? (
            <div className="mt-4">
              <Button type="button" variant="tonal" onClick={openFolaFulfillmentAssistant}>
                {t("prescriptions.fulfillment.getFolaHelp", "Ask Fola for fulfilment help")}
              </Button>
            </div>
          ) : null}

          {!isDoctorActor && usingFulfillmentFlow ? (
            <>
              {fulfillmentStatus === "awaiting_payment" ? (
                <>
                  <Divider insetStart={0} insetEnd={0} className="mt-4" variant="outlineVariant" />
                  <div className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div>
                      <div className="text-label-large text-on-surface-var">
                        {t("prescriptions.fulfillment.selectedPharmacy", "Selected pharmacy")}
                      </div>
                      <div className="mt-2 text-title-medium text-on-surface">
                        {fulfillment?.pharmacyName || t("prescriptions.fulfillment.afrimedPharmacy", "AfriMed™ Pharmacy")}
                      </div>
                    </div>
                    <div>
                      <div className="text-label-large text-on-surface-var">
                        {t("prescriptions.fulfillment.totalCost", "Total cost")}
                      </div>
                      <div className="mt-2 text-title-medium text-on-surface">
                        {formatCurrency(fulfillment?.price, fulfillmentCurrency)}
                      </div>
                    </div>
                  </div>

                  <Divider insetStart={0} insetEnd={0} className="mt-4" variant="outlineVariant" />
                  <div className="mt-4 text-body-medium text-on-surface">
                    {t(
                      "prescriptions.fulfillment.paymentReadyBody",
                      "Payment is ready. Complete checkout to confirm this pharmacy order and let the branch release it for pickup.",
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button type="button" onClick={() => nav(`/appointments/prescriptions/${rx.id}/pay`)}>
                      {t("prescriptions.fulfillment.payNow", "Pay pharmacy now")}
                    </Button>
                  </div>
                </>
              ) : fulfillmentPaid ? (
                <>
                  <Divider insetStart={0} insetEnd={0} className="mt-4" variant="outlineVariant" />
                  <div className="mt-4 text-body-medium text-[#008A8F]">
                    {t(
                      fulfillmentIsDelivery
                        ? "prescriptions.fulfillment.paymentConfirmedDeliveryBody"
                        : "prescriptions.fulfillment.paymentConfirmedBody",
                      fulfillmentIsDelivery
                        ? "Payment is confirmed. {pharmacy} has been notified and is arranging delivery of this order."
                        : "Payment is confirmed. {pharmacy} has been notified and will release this order once it is ready for pickup.",
                      { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                    )}
                  </div>
                </>
              ) : fulfillmentReadyForPickup ? null : fulfillmentStatus === "preparing" || fulfillmentStatus === "pharmacy_selected" ? (
                <>
                  <Divider insetStart={0} insetEnd={0} className="mt-4" variant="outlineVariant" />
                  <div className="mt-4 text-body-medium text-[#008A8F]">
                    {fulfillmentStatus === "preparing"
                      ? t(
                          "prescriptions.fulfillment.preparingBody",
                          "{pharmacy} accepted this prescription and is preparing it.",
                          { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                        )
                      : t(
                          "prescriptions.fulfillment.reviewingBody",
                          "{pharmacy} has been notified and is reviewing this prescription.",
                          { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.yourSelectedPharmacy", "Your selected pharmacy") },
                        )}
                  </div>
                </>
              ) : (
                <div className="mt-4 space-y-4">
                  {fulfillmentStatus === "rejected" ? (
                    <div className="text-body-medium text-[rgb(var(--clr-error))]">
                      {t(
                        "prescriptions.fulfillment.rejectedBody",
                        "{pharmacy} could not fulfil this prescription. Please choose another pharmacy.",
                        { pharmacy: fulfillment?.pharmacyName || t("prescriptions.fulfillment.theSelectedPharmacy", "The selected pharmacy") },
                      )}
                    </div>
                  ) : null}

                  <Divider insetStart={0} insetEnd={0} variant="outlineVariant" />

                  <div className="pt-1">
                    <div className="text-label-large text-on-surface-var">
                      {t("prescriptions.fulfillment.nearbyPharmacies", "Nearby pharmacies")}
                    </div>
                    <div className="mt-2 text-body-medium text-on-surface-var">
                      {pharmacyOptionsLoading
                        ? t("prescriptions.fulfillment.loadingNearby", "Loading the 3 closest signed-up pharmacies…")
                        : pharmacyOptions.length
                          ? patientGeo.status === "granted"
                            ? t("prescriptions.fulfillment.chooseNearby", "Choose one of the 3 closest pharmacies to continue.")
                            : t(
                                "prescriptions.fulfillment.chooseRanked",
                                "Choose a signed-up pharmacy to continue. AfriMed™ will rank by distance once location is available.",
                              )
                          : t("prescriptions.fulfillment.noneAvailable", "No signed-up pharmacy branches are available to request from yet.")}
                    </div>

                    {pharmacyOptions.length ? (
                      <>
                        <div className="mt-4 grid gap-3">
                          {pharmacyOptions.map((branch) => {
                            const ownerUserId = String(branch?.owner_user_id || "").trim();
                            const isSelected = ownerUserId === selectedPharmacyOwnerUserId;
                            const mapHref = buildPharmacyMapHref(branch);
                            return (
                              <Card
                                key={ownerUserId}
                                role="button"
                                tabIndex={0}
                                onClick={() => selectPharmacyBranch(ownerUserId)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    selectPharmacyBranch(ownerUserId);
                                  }
                                }}
                                className={[
                                  "w-full cursor-pointer px-4 py-4 text-left transition",
                                  isSelected
                                  ? "border bg-[rgba(var(--afrimed-tertiary),0.16)] border-[rgba(var(--afrimed-tertiary),0.45)] transition-all duration-500"
                                    : "",
                                ].join(" ")}
                              >
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                  <div>
                                    <div className="text-title-small text-on-surface">
                                      {displayPharmacyBranchName(branch)}
                                    </div>
                                    <div className="mt-1 text-body-small text-on-surface-var">
                                      {formatPharmacyBranchLocation(branch) || t("prescriptions.fulfillment.locationPending", "Location details pending")}
                                    </div>
                                    {branch?.distance_km != null ? (
                                      <div className="mt-1 text-label-small text-[#008A8F]">
                                        {formatDistanceKm(branch.distance_km, t)}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <span className="rounded-full bg-[rgba(24,50,119,0.08)] px-3 py-1 text-label-small text-on-surface-var">
                                      {branch?.pickup_enabled
                                        ? t("prescriptions.fulfillment.modePickup", "Pickup")
                                        : t("prescriptions.fulfillment.pickupPending", "Pickup pending")}
                                    </span>
                                    {branch?.delivery_enabled ? (
                                      <span className="rounded-full bg-[rgba(0,189,196,0.12)] px-3 py-1 text-label-small text-[#008A8F]">
                                        {t("prescriptions.fulfillment.modeDelivery", "Delivery")}
                                      </span>
                                    ) : null}
                                    {mapHref ? (
                                      <a
                                        href={mapHref}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                        className="rounded-full bg-[rgba(24,50,119,0.08)] px-3 py-1 text-label-small text-primary hover:underline"
                                      >
                                        {t("prescriptions.fulfillment.seeOnMap", "See on map")}
                                      </a>
                                    ) : null}
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>

                        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                              type="button"
                              variant={requestedFulfillmentMode === "pickup" ? "default" : "tonal"}
                              onClick={() => setRequestedFulfillmentMode("pickup")}
                            >
                              {t("prescriptions.fulfillment.modePickup", "Pickup")}
                            </Button>
                            <Button
                              type="button"
                              variant={requestedFulfillmentMode === "delivery" ? "default" : "tonal"}
                              onClick={() => setRequestedFulfillmentMode("delivery")}
                              disabled={!deliveryAllowed}
                            >
                              {t("prescriptions.fulfillment.modeDelivery", "Delivery")}
                            </Button>
                          </div>
                          <Button
                            type="button"
                            disabled={!selectedPharmacyOwnerUserId || !requestedFulfillmentMode || requestBusy}
                            onClick={handleRequestPharmacy}
                          >
                            {requestBusy
                              ? t("prescriptions.fulfillment.selectingPharmacy", "Selecting pharmacy…")
                              : t("prescriptions.fulfillment.selectPharmacy", "Select pharmacy")}
                          </Button>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          ) : pharmacyQuoteReady ? (
            <>
                  <Divider insetStart={0} insetEnd={0} className="mt-4" variant="outlineVariant" />
              <div className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-3">
                <div>
                  <div className="text-label-large text-on-surface-var">
                    {t("prescriptions.fulfillment.medicineSubtotal", "Medicine subtotal")}
                  </div>
                  <div className="mt-2 text-title-medium text-on-surface">
                    {formatCurrencyFromCents(
                      pharmacyPayment?.medicineSubtotalCents,
                      pharmacyPayment?.currency,
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-label-large text-on-surface-var">
                    {t("prescriptions.fulfillment.modeDelivery", "Delivery")}
                  </div>
                  <div className="mt-2 text-title-medium text-on-surface">
                    {formatCurrencyFromCents(
                      pharmacyPayment?.deliveryFeeCents,
                      pharmacyPayment?.currency,
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-label-large text-on-surface-var">
                    {t("prescriptions.fulfillment.total", "Total")}
                  </div>
                  <div className="mt-2 text-title-medium text-on-surface">
                    {formatCurrencyFromCents(pharmacyTotalCents, pharmacyPayment?.currency)}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-body-small text-on-surface-var">
                {pharmacyPayment?.fulfillmentMode === "delivery"
                  ? t("prescriptions.fulfillment.quoteIncludesDelivery", "This quote includes delivery.")
                  : t("prescriptions.fulfillment.quotePreparedForPickup", "This quote is prepared for pickup.")}
                {pharmacyPayment?.quotedAt
                  ? ` ${t("prescriptions.fulfillment.quotedOn", "Quoted {date}.", {
                      date: fmtDate(pharmacyPayment.quotedAt),
                    })}`
                  : ""}
              </div>

              {!isDoctorActor ? (
                <div className="mt-4 flex flex-wrap gap-3">
                  {!pharmacyPaid && !pharmacyRefundPending && !pharmacyRefunded ? (
                    <Button
                      type="button"
                      onClick={() => nav(`/appointments/prescriptions/${rx.id}/pay`)}
                    >
                      {t("prescriptions.fulfillment.payNow", "Pay pharmacy now")}
                    </Button>
                  ) : null}

                  {pharmacyPaid ? (
                    <Button
                      type="button"
                      variant="tonal"
                      onClick={handleCancelPharmacyOrder}
                      disabled={refundBusy}
                    >
                      {refundBusy
                        ? t("prescriptions.fulfillment.cancellingOrder", "Cancelling order…")
                        : t("prescriptions.fulfillment.cancelOrder", "Cancel pharmacy order")}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <div className="mt-4 space-y-4">
              {pharmacyRequestPending ? (
                <div className="text-body-medium text-[#008A8F]">
                  {t(
                    "prescriptions.fulfillment.requestedQuoteBody",
                    "{pharmacy} has been asked to prepare a {mode} quote.",
                    {
                      pharmacy:
                        pharmacyRequest?.requestedPharmacyName ||
                        t("prescriptions.fulfillment.requestedPharmacy", "Requested pharmacy"),
                      mode:
                        pharmacyRequest?.requestedFulfillmentMode === "delivery"
                          ? t("prescriptions.fulfillment.modeDeliveryLower", "delivery")
                          : t("prescriptions.fulfillment.modePickupLower", "pickup"),
                    },
                  )}
                  {pharmacyRequest?.requestedAt ? ` Requested ${fmtDate(pharmacyRequest.requestedAt)}.` : ""}
                </div>
              ) : null}

              <Divider insetStart={0} insetEnd={0} variant="outlineVariant" />

              <div className="pt-1">
                <div className="text-label-large text-on-surface-var">
                  {t("prescriptions.fulfillment.signedUpPharmacies", "Signed-up pharmacies")}
                </div>
                <div className="mt-2 text-body-medium text-on-surface-var">
                  {pharmacyOptionsLoading
                    ? t("prescriptions.fulfillment.loadingBranches", "Loading available pharmacy branches…")
                    : pharmacyOptions.length
                      ? t(
                          "prescriptions.fulfillment.selectBranchCountry",
                          "Select the pharmacy branch that should prepare your prescription. AfriMed™ currently shows eligible pharmacies in the same country while precise location matching is being expanded.",
                        )
                      : t("prescriptions.fulfillment.noneAvailable", "No signed-up pharmacy branches are available to request from yet.")}
                </div>

                {pharmacyOptions.length ? (
                  <>
                    <div className="mt-4 grid gap-3">
                      {pharmacyOptions.map((branch) => {
                        const ownerUserId = String(branch?.owner_user_id || "").trim();
                        const isSelected = ownerUserId === selectedPharmacyOwnerUserId;
                        const mapHref = buildPharmacyMapHref(branch);
                        return (
                          <Card
                            key={ownerUserId}
                            role="button"
                            tabIndex={0}
                            onClick={() => selectPharmacyBranch(ownerUserId)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                selectPharmacyBranch(ownerUserId);
                              }
                            }}
                            className={[
                              "w-full cursor-pointer px-4 py-4 text-left transition",
                              isSelected
                                ? "border bg-[rgba(var(--afrimed-tertiary),0.16)] border-[rgba(var(--afrimed-tertiary),0.45)] transition-all duration-500"
                                : "",
                            ].join(" ")}
                          >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <div className="text-title-small text-on-surface">
                                  {displayPharmacyBranchName(branch)}
                                </div>
                                <div className="mt-1 text-body-small text-on-surface-var">
                                  {formatPharmacyBranchLocation(branch) || t("prescriptions.fulfillment.locationPending", "Location details pending")}
                                </div>
                                {branch?.distance_km != null ? (
                                  <div className="mt-1 text-label-small text-[#008A8F]">
                                    {formatDistanceKm(branch.distance_km, t)}
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <span className="rounded-full bg-[rgba(24,50,119,0.08)] px-3 py-1 text-label-small text-on-surface-var">
                                  {branch?.pickup_enabled
                                    ? t("prescriptions.fulfillment.modePickup", "Pickup")
                                    : t("prescriptions.fulfillment.pickupPending", "Pickup pending")}
                                </span>
                                {branch?.delivery_enabled ? (
                                  <span className="rounded-full bg-[rgba(0,189,196,0.12)] px-3 py-1 text-label-small text-[#008A8F]">
                                    {t("prescriptions.fulfillment.modeDelivery", "Delivery")}
                                  </span>
                                ) : null}
                                {mapHref ? (
                                  <a
                                    href={mapHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    className="rounded-full bg-[rgba(24,50,119,0.08)] px-3 py-1 text-label-small text-primary hover:underline"
                                  >
                                    {t("prescriptions.fulfillment.seeOnMap", "See on map")}
                                  </a>
                                ) : null}
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>

                    <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                          type="button"
                          variant={requestedFulfillmentMode === "pickup" ? "default" : "tonal"}
                          onClick={() => setRequestedFulfillmentMode("pickup")}
                        >
                          {t("prescriptions.fulfillment.modePickup", "Pickup")}
                        </Button>
                        <Button
                          type="button"
                          variant={requestedFulfillmentMode === "delivery" ? "default" : "tonal"}
                          onClick={() => setRequestedFulfillmentMode("delivery")}
                          disabled={!deliveryAllowed}
                        >
                          {t("prescriptions.fulfillment.modeDelivery", "Delivery")}
                        </Button>
                      </div>
                      <Button
                        type="button"
                        disabled={!selectedPharmacyOwnerUserId || !requestedFulfillmentMode || requestBusy}
                        onClick={handleRequestPharmacy}
                      >
                        {requestBusy
                          ? t("prescriptions.fulfillment.sendingRequest", "Sending request…")
                          : t("prescriptions.fulfillment.requestQuote", "Request pharmacy quote")}
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          )}

          {rxError ? (
            <div className="mt-4 rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
              {rxError}
            </div>
          ) : null}
        </div>

        {/* QR overlay (mobile sheet + desktop dialog) */}
        <QrOverlay
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          rx={rx}
          qrSrc={qrSrc}
          qrElephantSrc={QR_ELEPHANT}
          sigPlaceholderSrc={SIG_PLACEHOLDER}
          stampBaseSrc={abs(DR_STAMP_BASE)}
          verifyPath={verifyPath}
          verifyUrl={qrValue}
          verifyState={verifyState}
        />
      </div>

      {/* PRINT / DOWNLOAD DOCUMENT (only shown in print + used by html2pdf) */}
      <div className="print-only">
        <div
          ref={printRef}
          className="print-paper print-reset-spacing h-auto mx-auto w-full max-w-[720px] px-4 pt-6 pb-6"
          style={{ fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial" }}
        >
          {/* Header row: Patient (left) + Doctor/Facility (right) */}
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="text-title-medium text-on-surface">
                {t("prescriptions.common.patient", "Patient")}
              </div>
              <div className="mt-1 text-body-medium text-on-surface">{rx.patient?.name || "—"}</div>
              {rx.patient?.careSubjectType === "dependant" && rx.patient?.guardianName ? (
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t("prescriptions.common.responsibleAdult", "Responsible adult")}:{" "}
                  <span className="text-on-surface">
                    {rx.patient.guardianName}
                    {rx.patient?.responsibleRelationship
                      ? ` • ${rx.patient.responsibleRelationship}`
                      : ""}
                  </span>
                </div>
              ) : null}
              {Array.isArray(rx.patient?.addressLines) && rx.patient.addressLines.length
                ? rx.patient.addressLines.map((line, index) => (
                    <div key={`${line}-${index}`} className="mt-1 text-body-small text-on-surface-var">
                      {line}
                    </div>
                  ))
                : rx.patient?.address && rx.patient.address !== "—" ? (
                    <div className="mt-1 text-body-small text-on-surface-var">{rx.patient.address}</div>
                  ) : null}
              {rx.patient?.dob ? (
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t("prescriptions.common.dob", "DOB")}:{" "}
                  <span className="text-on-surface">{fmtShortDate(rx.patient.dob)}</span>
                </div>
              ) : null}
              {rx.patient?.nationalId ? (
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t("prescriptions.common.id", "ID")}:{" "}
                  <span className="text-on-surface">{rx.patient.nationalId}</span>
                </div>
              ) : null}
            </div>

            <div className="min-w-0 text-right">
              <div className="text-title-medium text-on-surface">
                {rx.doctor?.facility?.name || t("prescriptions.common.facility", "Facility")}
              </div>
              <div className="mt-1 text-body-medium text-on-surface">
                {rx.doctor?.prescriptionName || rx.doctor?.name || "—"}
              </div>
              <div className="mt-1 text-body-small text-on-surface-var">
                {t("prescriptions.common.registrationShort", "Reg")}:{" "}
                <span className="text-on-surface">{rx.doctor?.regNo || "—"}</span>
              </div>
              {rx.doctor?.facility?.address && rx.doctor.facility.address !== "—" ? (
                <div className="mt-1 text-body-small text-on-surface-var">{rx.doctor.facility.address}</div>
              ) : null}
              {rx.doctor?.facility?.phone || rx.doctor?.facility?.email ? (
                <div className="mt-1 text-body-small text-on-surface-var">
                  {rx.doctor?.facility?.phone || ""}
                  {rx.doctor?.facility?.phone && rx.doctor?.facility?.email ? " · " : ""}
                  {rx.doctor?.facility?.email || ""}
                </div>
              ) : null}

              <div className="mt-3 flex justify-end">
                <div className="h-[80px] w-[80px] overflow-hidden ml-auto">
                  <img
                    src={qrSrc || abs(QR_OVERLAY)}
                    alt={t("prescriptions.common.verificationQr", "Verification QR")}
                    className="h-full w-full object-contain"
                    draggable="false"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Subject line */}
          <div className="mt-6">
            <div className="text-title-large text-on-surface">
              {t("prescriptions.common.ePrescription", "E-Prescription")}
            </div>
            <div className="mt-1 text-body-medium text-on-surface-var">
              {t("prescriptions.common.prescriptionNumber", "Prescription No")}:{" "}
              <span className="text-on-surface">{rx.rxNo || "—"}</span>
              {" · "}
              {t("prescriptions.common.issued", "Issued")}:{" "}
              <span className="text-on-surface">{fmtDate(rx.issuedAt)}</span>
            </div>
          </div>

          {/* Medication */}
          <div className="mt-6">
            {medicationRows.length > 1 ? (
              <div className="space-y-5">
                {medicationRows.map((item) => (
                  <div key={item.key}>
                    <div className="text-title-large text-on-surface">
                      {item.name || t("prescriptions.common.medication", "Medication")}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface">
                      {item.form || "—"}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {item.composition || "—"}
                    </div>

                    <div className="mt-4">
                      <div className="text-label-large text-on-surface-var">
                        {t("prescriptions.wizard.schedule", "Schedule")}
                      </div>

                      <div className="mt-3">
                        {item.rows.map((row, index) => (
                          <div key={`${item.key}-print-${index}`}>
                            <div className="flex items-start justify-between gap-6 py-2">
                              <div className="text-body-medium text-on-surface">{row}</div>
                            </div>
                            {index < item.rows.length - 1 ? <div className="h-px w-full bg-[rgba(2,6,23,0.08)]" /> : null}
                          </div>
                        ))}
                      </div>

                      {item.note ? (
                        <div className="mt-3 text-body-medium text-on-surface whitespace-pre-wrap">
                          {item.note}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="text-title-large text-on-surface">{rx.medicineName}</div>
                <div className="mt-1 text-body-medium text-on-surface">{rx.form}</div>
                <div className="mt-1 text-body-medium text-on-surface-var">{rx.ingredients}</div>

                <div className="mt-6">
                  <div className="text-label-large text-on-surface-var">
                    {t("prescriptions.wizard.schedule", "Schedule")}
                  </div>

                  <div className="mt-3">
                    <div className="flex items-start justify-between gap-6 py-2">
                      <div className="text-label-medium text-on-surface-var">
                        {t("prescriptions.wizard.frequency.title", "Frequency")}
                      </div>
                      <div className="text-body-medium text-on-surface text-right">{rx.frequencyLabel}</div>
                    </div>
                    <div className="h-px w-full bg-[rgba(2,6,23,0.08)]" />

                    <div className="flex items-start justify-between gap-6 py-2">
                      <div className="text-label-medium text-on-surface-var">
                        {t("prescriptions.wizard.dosage", "Dosage")}
                      </div>
                      <div className="text-body-medium text-on-surface text-right">{rx.dosageLabel}</div>
                    </div>
                    <div className="h-px w-full bg-[rgba(2,6,23,0.08)]" />

                    <div className="flex items-start justify-between gap-6 py-2">
                      <div className="text-label-medium text-on-surface-var">
                        {t("prescriptions.wizard.starts", "Starts")}
                      </div>
                      <div className="text-body-medium text-on-surface text-right">{fmtDate(rx.startsOn)}</div>
                    </div>
                    <div className="h-px w-full bg-[rgba(2,6,23,0.08)]" />

                    <div className="flex items-start justify-between gap-6 py-2">
                      <div className="text-label-medium text-on-surface-var">
                        {t("prescriptions.wizard.ends", "Ends")}
                      </div>
                      <div className="text-body-medium text-on-surface text-right">{fmtDate(rx.endsOn)}</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Signature + stamp must stay together with disclaimer (no orphan page 2) */}
          <div className="mt-6 print-avoid-break">
            {/* dotted line WITH assets sitting ON the line */}
            <SignatureStampLine
              sigSrc={abs(rx.doctor.signatureSrc || SIG_PLACEHOLDER)}
              stampBaseSrc={abs(DR_STAMP_BASE)}
              doctorName={rx.doctor.prescriptionName || rx.doctor.name}
              regNo={rx.doctor.regNo}
              sigClassName="h-[68px] w-[160px]"
              stampSizePx={70}
              sigNudgeY={-4}
              stampNudgeY={-4}
            />

            <div className="mt-3 text-body-small text-on-surface-var">
              {t(
                "prescriptions.common.poweredByIssuedVia",
                "Powered by {issuedVia}. The prescriber remains responsible for clinical decisions.",
                { issuedVia: rx.issuedVia },
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
